/**
 * QuestionCard - Display a question with options and actions
 * Used in admin TestManagement to show question details with edit/delete actions
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit, Trash2, HelpCircle } from 'lucide-react';

const QuestionCard = ({ 
  question, 
  index,
  isSelected = false,
  onSelectChange,
  onEdit, 
  onDelete,
  showSelection = true,
  showActions = true
}) => {
  const q = question;
  
  return (
    <Card data-testid={`question-card-${q.id}`}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3 flex-1">
            {showSelection && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onSelectChange}
                data-testid={`question-checkbox-${q.id}`}
              />
            )}
            <h4 className="font-semibold text-gray-800">Q{index + 1}. {q.question_text}</h4>
          </div>
          {showActions && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-question-${q.id}`}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={onDelete} data-testid={`delete-question-${q.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Display based on question type */}
        {q.question_type === 'combo' ? (
          /* Combo MCQ Display */
          <div className="space-y-3 mt-3">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r space-y-2">
              <p className="text-xs font-semibold text-blue-900">Statements:</p>
              <div className="space-y-1 text-sm">
                <div><strong>A.</strong> {q.stmt_a}</div>
                <div><strong>B.</strong> {q.stmt_b}</div>
                <div><strong>C.</strong> {q.stmt_c}</div>
                <div><strong>D.</strong> {q.stmt_d}</div>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-xs font-semibold text-gray-700">Combination Options:</p>
              <div className={q.correct_combo_option === 'a' ? 'text-green-600 font-semibold' : ''}>(a) {q.combo_option_a}</div>
              <div className={q.correct_combo_option === 'b' ? 'text-green-600 font-semibold' : ''}>(b) {q.combo_option_b}</div>
              <div className={q.correct_combo_option === 'c' ? 'text-green-600 font-semibold' : ''}>(c) {q.combo_option_c}</div>
              <div className={q.correct_combo_option === 'd' ? 'text-green-600 font-semibold' : ''}>(d) {q.combo_option_d}</div>
            </div>
          </div>
        ) : (
          /* Simple MCQ Display */
          <div className="grid grid-cols-2 gap-2 text-sm mt-3">
            <div className={q.correct_answer === 'a' ? 'text-green-600 font-semibold' : ''}>A. {q.option_a}</div>
            <div className={q.correct_answer === 'b' ? 'text-green-600 font-semibold' : ''}>B. {q.option_b}</div>
            <div className={q.correct_answer === 'c' ? 'text-green-600 font-semibold' : ''}>C. {q.option_c}</div>
            <div className={q.correct_answer === 'd' ? 'text-green-600 font-semibold' : ''}>D. {q.option_d}</div>
          </div>
        )}
        
        {q.explanation && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-gray-700">{q.explanation}</p>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-2">ID: {q.id}</p>
      </CardContent>
    </Card>
  );
};

export default QuestionCard;
